import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { IconButton, useClickOutside } from 'qampo-ui-library';
import { IconClose } from '../../UI';
import type { Intervention, WeekData } from '../../../types/weekOverview';
import {
  INTERVENTION_CARD_SELECTOR,
  PROGRAM_LABEL_DISPLAY_NAMES,
  SESSION_TYPE_TRANSLATION_KEYS,
  WEEKDAY_FULL_TRANSLATION_KEYS
} from '../../../constants/weekOverviewConstants';
import { formatMinutesAsTime } from '../../../utils/weekDateUtils';
import { patientDisplayName } from '../../../services/patientLabelService';
import { computePopoverPosition } from '../../../utils/popoverPositionUtils';
import type { PopoverPosition } from '../../../utils/popoverPositionUtils';
import styles from './InterventionDetailPopover.module.css';

interface InterventionDetailPopoverProps {
  intervention: Intervention;
  weekData: WeekData;
  anchorRect: DOMRect;
  onClose: () => void;
}

// Horizontal distance between the anchor card and the popover.
const POPOVER_ANCHOR_GAP_PX = 12;
// Minimum space kept between the popover and every viewport edge.
const POPOVER_VIEWPORT_MARGIN_PX = 12;
// Placeholder for detail fields that have no value.
const EMPTY_VALUE = '—';

export const InterventionDetailPopover = ({
  intervention,
  weekData,
  anchorRect,
  onClose
}: InterventionDetailPopoverProps) => {
  const { t } = useTranslation();
  const popoverRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<PopoverPosition>({
    left: anchorRect.right + POPOVER_ANCHOR_GAP_PX,
    top: anchorRect.top
  });
  const [isMeasured, setIsMeasured] = useState(false);

  const staffById = useMemo(() => {
    const map = new Map<string, string>();
    for (const s of weekData.staff) map.set(s.id, s.name);
    return map;
  }, [weekData.staff]);

  const patientsById = useMemo(() => {
    const map = new Map<string, string>();
    for (const p of weekData.patients) map.set(p.id, patientDisplayName(p, t));
    return map;
  }, [weekData.patients, t]);

  const skillsById = useMemo(() => {
    const map = new Map<string, string>();
    for (const s of weekData.skills) map.set(s.id, s.name);
    return map;
  }, [weekData.skills]);

  const room = weekData.rooms.find((r) => r.id === intervention.roomId);

  // The popover renders hidden first, then is measured and placed beside the
  // anchor — placing before measuring would flash it at the wrong position.
  useLayoutEffect(() => {
    const popover = popoverRef.current;
    if (!popover) return;
    const rect = popover.getBoundingClientRect();
    setPosition(
      computePopoverPosition({
        anchor: anchorRect,
        popoverWidth: rect.width,
        popoverHeight: rect.height,
        viewportWidth: window.innerWidth,
        viewportHeight: window.innerHeight,
        anchorGap: POPOVER_ANCHOR_GAP_PX,
        viewportMargin: POPOVER_VIEWPORT_MARGIN_PX
      })
    );
    setIsMeasured(true);
  }, [anchorRect]);

  // Escape is handled one level up: IndividualCalendarModal's close handler
  // dismisses the popover before the modal itself.
  useClickOutside(popoverRef, (event) => {
    const target = event?.target;
    // Clicking another intervention card swaps the popover via the card's own
    // click handler — closing here too would race that swap. Element (not
    // HTMLElement): the mousedown can land on a card's SVG session icon.
    if (target instanceof Element && target.closest(INTERVENTION_CARD_SELECTOR)) return;
    onClose();
  });

  // The popover is fixed-position, so any scroll would detach it from its
  // anchor card — close instead of drifting. Armed one frame after the anchor
  // changes: the click that opens (or swaps) the popover can itself scroll the
  // calendar — the browser reveals the focused card — and that scroll event
  // arrives after mount, so acting on it would close the popover in the same
  // instant it opened.
  useEffect(() => {
    let isArmed = false;
    const armFrameId = requestAnimationFrame(() => {
      isArmed = true;
    });
    const handleScroll = () => {
      if (isArmed) onClose();
    };
    document.addEventListener('scroll', handleScroll, true);
    return () => {
      cancelAnimationFrame(armFrameId);
      document.removeEventListener('scroll', handleScroll, true);
    };
  }, [onClose, anchorRect]);

  return createPortal(
    <div
      ref={popoverRef}
      className={styles.popover}
      style={{
        left: position.left,
        top: position.top,
        visibility: isMeasured ? 'visible' : 'hidden'
      }}
      role="dialog"
    >
      <header className={styles.header}>
        <div className={styles.titleColumn}>
          <h2 className={styles.title}>{intervention.title}</h2>
          <p className={styles.subtitle}>
            {t(SESSION_TYPE_TRANSLATION_KEYS[intervention.sessionType])}
            {` · ${t(WEEKDAY_FULL_TRANSLATION_KEYS[intervention.day])}`}
          </p>
        </div>
        <span aria-label={t('close-details')} title={t('close-details')}>
          <IconButton
            icon={<IconClose className={styles.closeIcon} />}
            onClick={onClose}
            size="small"
            iconColor="var(--color-text-primary)"
            buttonBgColor="var(--color-surface-primary)"
            classes={styles.closeButton}
          />
        </span>
      </header>
      <div className={styles.content}>
        <div className={styles.row}>
          <span className={styles.label}>{t('week-overview-detail-time')}</span>
          <span className={styles.value}>
            {formatMinutesAsTime(intervention.startMinutes)} –{' '}
            {formatMinutesAsTime(intervention.endMinutes)}
          </span>
        </div>
        <div className={styles.row}>
          <span className={styles.label}>{t('week-overview-detail-room')}</span>
          <span className={styles.value}>{room?.name ?? EMPTY_VALUE}</span>
        </div>
        {intervention.programLabel && (
          <div className={styles.row}>
            <span className={styles.label}>{t('week-overview-detail-program')}</span>
            <span className={styles.value}>
              {PROGRAM_LABEL_DISPLAY_NAMES[intervention.programLabel]}
            </span>
          </div>
        )}
        <div className={styles.row}>
          <span className={styles.label}>{t('week-overview-detail-staff')}</span>
          <div className={styles.list}>
            {intervention.staffIds.length === 0 ? (
              <span className={styles.empty}>{EMPTY_VALUE}</span>
            ) : (
              intervention.staffIds.map((id) => (
                <span key={id} className={styles.listItem}>
                  {staffById.get(id) ?? id}
                </span>
              ))
            )}
          </div>
        </div>
        <div className={styles.row}>
          <span className={styles.label}>{t('week-overview-detail-patients')}</span>
          <div className={styles.list}>
            {intervention.patientIds.length === 0 ? (
              <span className={styles.empty}>{t('week-overview-detail-no-patients')}</span>
            ) : (
              intervention.patientIds.map((id) => (
                <span key={id} className={styles.listItem}>
                  {patientsById.get(id) ?? id}
                </span>
              ))
            )}
          </div>
        </div>
        <div className={styles.row}>
          <span className={styles.label}>{t('week-overview-detail-skills')}</span>
          <div className={styles.list}>
            {intervention.requiredSkillIds.length === 0 ? (
              <span className={styles.empty}>{EMPTY_VALUE}</span>
            ) : (
              intervention.requiredSkillIds.map((id) => (
                <span key={id} className={styles.listItem}>
                  {skillsById.get(id) ?? id}
                </span>
              ))
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};
